export interface GeocodeQuery {
	street?: string;
	number?: string;
	city?: string;
	state?: string;
	zipcode?: string;
}

export interface GeocodePoint {
	lat: number;
	lng: number;
}

interface NominatimResult {
	lat: string;
	lon: string;
}

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

// geocodeAddress forward-geocodes a structured Brazilian address into an
// approximate point via OpenStreetMap's Nominatim — the same project that
// serves the map's raster tiles, so no API key is needed. The result centers
// the map near the address; it is NOT precise enough to save as the pin, so
// callers use it only to move the camera and let the user click the exact spot.
// Returns null when there is too little to search on or nothing is found.
export async function geocodeAddress(
	query: GeocodeQuery,
): Promise<GeocodePoint | null> {
	const street = [query.number?.trim(), query.street?.trim()]
		.filter(Boolean)
		.join(" ");
	const city = query.city?.trim();
	const state = query.state?.trim();
	const postalcode = query.zipcode?.replace(/\D/g, "");

	if (!street && !city && !postalcode) return null;

	const params = new URLSearchParams({
		format: "jsonv2",
		limit: "1",
		countrycodes: "br",
	});
	if (street) params.set("street", street);
	if (city) params.set("city", city);
	if (state) params.set("state", state);
	if (postalcode) params.set("postalcode", postalcode);

	const response = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
		headers: { Accept: "application/json" },
	});
	if (!response.ok) return null;

	const results = (await response.json()) as NominatimResult[];
	const first = results[0];
	if (!first) return null;

	const lat = Number.parseFloat(first.lat);
	const lng = Number.parseFloat(first.lon);
	if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

	return { lat, lng };
}
